import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  memo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  IconButton,
  Icon,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useNavigate } from "react-router-dom";
import { styled, alpha, useTheme } from "@mui/material/styles";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import customStyles from "app/shared-components/SelectCustomStyle";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import FormPageHeader from "../../utilities/FormPageHeader";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jwtDecode from "jwt-decode";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.min.css";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CloseIcon from "@mui/icons-material/Close";

const KEY_BACKSPACE = "Backspace";
const KEY_ENTER = "Enter";
const KEY_TAB = "Tab";

const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.secondary.main,
  padding: "6px 16px",
  borderRadius: "20px",
  cursor: "pointer",
  display: "block",
  textAlign: "center",
  position: "relative",
  fontSize: "13px",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: theme.palette.secondary.dark,
  },

  "&[disabled]": {
    opacity: 0.4,
    background: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
    "& .file": {
      cursor: "not-allowed",
    },
  },
}));

const TOAST_SEVERITY_ICONS = {
  error: ErrorOutlineOutlinedIcon,
  warning: WarningAmberOutlinedIcon,
};

const ModalToast = ({ message, show, severity = "error", onClose }) => {
  const theme = useTheme();
  if (!show) return null;
  const ToastIcon = TOAST_SEVERITY_ICONS[severity] || TOAST_SEVERITY_ICONS.error;
  const accentColor = severity === "warning" ? theme.palette.warning.main : theme.palette.error.main;
  return (
    <Box sx={{ position: "absolute", top: { xs: 64, sm: 24 }, left: "50%", transform: "translateX(-50%)", zIndex: 50, width: "calc(100% - 32px)", maxWidth: 360 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, border: `4px solid ${accentColor}`, pl: { xs: 2, sm: 3 }, pr: 1, py: 2, borderRadius: 2, boxShadow: theme.shadows[4], width: "100%" }}>
        <ToastIcon sx={{ color: accentColor, mr: 1, fontSize: 24, mt: "2px" }} />
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, pt: "2px", wordBreak: "break-word" }}>{message}</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Dismiss message" sx={{ ml: 1, mt: "-4px" }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

const NumericEditor = memo(forwardRef((props, ref) => {
  const createInitialState = () => {
    let startValue = props.value;
    let highlightAllOnFocus = true;
    if (props.eventKey === KEY_BACKSPACE) startValue = "";
    else if (props.eventKey && props.eventKey.length === 1 && "1234567890".indexOf(props.eventKey) > -1) {
      startValue = props.eventKey;
      highlightAllOnFocus = false;
    }
    return { value: startValue, highlightAllOnFocus };
  };
  const initialState = createInitialState();
  const [value, setValue] = useState(initialState.value);
  const refInput = useRef(null);

  useEffect(() => {
    const eInput = refInput.current;
    eInput.focus();
    if (initialState.highlightAllOnFocus) eInput.select();
  }, []);

  const onKeyDown = (event) => {
    if (["ArrowLeft", "ArrowRight", KEY_BACKSPACE].indexOf(event.key) > -1) {
      event.stopPropagation();
      return;
    }
    if (event.key !== KEY_ENTER && event.key !== KEY_TAB && !/\d/.test(event.key)) {
      event.preventDefault();
    }
    if (event.key === KEY_ENTER || event.key === KEY_TAB) {
      props.stopEditing();
    }
  };

  useImperativeHandle(ref, () => ({
    getValue() { return value === "" || value == null ? null : parseInt(value); }
  }));

  return (
    <input ref={refInput} value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={onKeyDown} className="numeric-input" inputMode="numeric" style={{ width: '100%', height: '100%', backgroundColor: 'inherit', color: 'inherit', border: 'none', outline: 'none', padding: '0 8px' }} />
  );
}));

const ExpectWeekEditor = memo(forwardRef((props, ref) => {
  const initialValue = props.value ? { label: props.value, value: props.value } : null;
  const [selectedWeek, setSelectedWeek] = useState(initialValue);
  const selectedWeekRef = useRef(initialValue);
  const [weekOption, setWeekOption] = useState([]);

  useEffect(() => {
    const getLoadingWeekList = async () => {
      try {
        const list = await OAuthService.getList(`${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.getWeek}?date=${moment().add(7, "weeks").format("YYYY-MM-DD")}`);
        if (list.data) {
          const result = [];
          let weeks = list.data;
          const startYear = moment().add(7, "weeks").year();
          for (let year = startYear; year <= startYear + 4; year++) {
            if (year !== startYear) weeks = 1;
            for (; weeks <= 52; weeks++) {
              result.push({ value: weeks, label: `${weeks}/${year}`, year });
            }
          }
          setWeekOption(result);
        }
      } catch (error) { console.error("Error fetching loading weeks", error); }
    };
    getLoadingWeekList();
  }, []);

  const handleChangeWeek = (option) => {
    setSelectedWeek(option);
    selectedWeekRef.current = option;
    if (props.node && props.node.data) props.node.setDataValue("year", option.year);
    if (props.api) props.api.stopEditing();
  };

  useImperativeHandle(ref, () => ({
    getValue() { return selectedWeekRef.current?.label || ""; }
  }));

  const cellWidth = props.eGridCell ? Math.max(props.eGridCell.clientWidth, 110) : 150;

  return (
    <div style={{ width: cellWidth }}>
      <Select
        value={selectedWeek}
        options={weekOption}
        onChange={handleChangeWeek}
        styles={{
          ...customStyles(),
          container: (provided) => ({ ...provided, width: "100%" }),
        }}
        menuPlacement="auto"
        menuPosition="fixed"
        defaultMenuIsOpen={true}
      />
    </div>
  );
}));

const newOrderUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.newCustomerOrder}`;
const companiesUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.companies}`;
const freightOptionUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.freightOptions}`;
const aoRoReasonsUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.aoRoReasons}`;
const searchProjectUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.searchProjectName}`;

const BulkPartOrder = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const isTouch = useMediaQuery("(pointer: coarse)", { noSsr: true });
  const comfortableRows = isMobile || isTouch;
  const hiddenExcelFile = useRef(null);
  const phaseGridRef = useRef();
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [showroom, setShowroom] = useState(null);
  const [showroomOptions, setShowroomOptions] = useState([]);
  const [product, setProduct] = useState(null);
  const [projectProduct, setProjectProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [orderCategoryValue, setOrderCategoryValue] = useState(null);
  const [orderCategoryOptions, setOrderCategoryOptions] = useState([]);
  const [defaultOrderReference, setDefaultOrderReference] = useState("");
  const [orderReference, setOrderReference] = useState("");
  const [containerNo, setContainerNo] = useState("");
  const [loadingWeek, setLoadingWeek] = useState(null);
  const [loadingWeekOptions, setLoadingWeekOptions] = useState([]);
  const [freight, setFreight] = useState(null);
  const [freightOptions, setFreightOptions] = useState([]);
  const [comment, setComment] = useState("");
  const [isFetchingConfig, setIsFetchingConfig] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedProName, setSelectedProName] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [existingProjectOptions, setExistingProjectOptions] = useState([]);
  const [currentQuery, setCurrentQuery] = useState("");
  const [selectedQty, setSelectedQty] = useState("");
  const [selectedPhases, setSelectedPhases] = useState("");
  const [originalQty, setOriginalQty] = useState(null);
  const [editPhases, setEditPhases] = useState(false);
  const [phasesRowData, setPhasesRowData] = useState([]);
  const [phasesTotalValue, setPhasesTotalValue] = useState(0);
  const [showPhasesAlert, setShowPhasesAlert] = useState(false);
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [phaseNameCode, setPhaseNameCode] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("error");
  const toastTimerRef = useRef(null);
  const [excelErrorDialog, setExcelErrorDialog] = useState({ open: false, title: "", rows: [] });
  const TOTAL_MAX_LENGTH = 21;
  const MIN_LOADING_VISIBLE_MS = 350;
  const showProjectEditControls = false;
  const pageSize = 30;
  const token = localStorage.getItem("token") ? jwtDecode(localStorage.getItem("token")) : null;
  const hasSupperUser = ["ROLE_ADMIN", "ROLE_MD", "ROLE_ORDERPRO", "ROLE_SOPDIR"].some((role) => token?.scope.includes(role));
  const isTaiwan = hasSupperUser ? selectedPartner?.value === 30058 : customerId === 30058;
  const phasesDataRef = useRef([]);
  phasesDataRef.current = phasesRowData;

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (hasSupperUser) {
          const compRes = await OAuthService.getList(`${companiesUrl}/search/findActiveByCustomerTypes?&customerTypes=REGULAR&customerTypes=WHOLESALE&page=0&size=1000`);
          const companies = compRes.data?._embedded?.companies || [];
          setCustomerOptions(companies.map(c => ({ value: c.id, label: `${c.id}-${c.shortKey}-${c.countryName}` })));
        }
        const freightRes = await OAuthService.getList(freightOptionUrl);
        const freights = freightRes?.data?.freightOptions?.map(f => ({ label: f.shippingMethodName, value: f.shippingMethodName, id: f.code }));
        setFreightOptions(freights);
        const seaFreight = freights.find(f => f.label === "Transportation By Sea");
        if (seaFreight) setFreight(seaFreight);
      } catch (error) { toast.error("Failed to load initial data"); }
    };
    fetchInitialData();
  }, [hasSupperUser]);

  useEffect(() => {
    const fetchOrderConfig = async () => {
      try {
        setIsFetchingConfig(true);
        const url = hasSupperUser && selectedPartner ? `${newOrderUrl}?customerId=${selectedPartner.value}` : newOrderUrl;
        if (!hasSupperUser || selectedPartner) {
          const res = await OAuthService.getList(url);
          const newOrder = res.data;
          setProductOptions(newOrder?.products.map(p => ({ label: p.name, value: p.shortKey, id: p.id })));
          setShowroomOptions(Object.keys(newOrder?.showrooms || {}).map(key => ({ value: key, label: newOrder.showrooms[key] })));
          setLoadingWeekOptions(Object.keys(newOrder?.loadingWeek || {}).map(key => ({ value: key, label: newOrder.loadingWeek[key] })));
          const filteredCategories = (newOrder?.orderCategories || []).filter(c => ["PRJ", "CPRJ"].includes(c.name)).map(c => ({ label: c.displayName, value: c.name }))
          setOrderCategoryOptions(filteredCategories);
          setCustomerName(newOrder?.companyName);
          setCustomerId(newOrder?.partnerCompany);
          setDefaultOrderReference(newOrder?.projectNum || "");
          setOrderReference(newOrder?.projectNum || "");
        }
      } catch (error) { toast.error("Failed to fetch order configuration"); }
      finally { setIsFetchingConfig(false); }
    };
    fetchOrderConfig();
  }, [selectedPartner, hasSupperUser]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const compId = hasSupperUser ? selectedPartner?.value : customerId;
        if (orderCategoryValue?.value && compId) {
          const url = `${searchProjectUrl}?companyId=${compId}&orderCategory=${orderCategoryValue.value}&page=0&size=${pageSize}`;
          const list = await OAuthService.getList(url);
          const data = list.data?.content || [];
          const opts = data.map((item) => ({
            value: item.id,
            label: item.projectName,
            phases: item.noOfPhases,
            quantity: item.totalQuantity,
            phasesData: item.projectPhases,
            orderReference: item.project,
            projectCode: item.clientName,
            types: item.types,
            vendorId: item.vendorId,
            vendor: item.vendor,
          }));
          setProjectOptions(opts);
          setExistingProjectOptions(opts);
        }
      } catch (error) { console.error("Failed to load projects", error); }
    };
    fetchProjects();
  }, [orderCategoryValue?.value, customerId, selectedPartner, hasSupperUser]);

  useEffect(() => {
    setIsSectionVisible(checkAllCellsFilled());
  }, [phasesRowData]);

  const handleProjectInputChange = (inputValue) => setCurrentQuery(inputValue);

  const handleChangeOrderCategory = (newValue) => {
    setOrderCategoryValue(newValue);
    phaseGridRef.current?.api?.deselectAll();
    setSelectedRows([]);
    setSelectedProName(null);
    setSelectedPhases("");
    setSelectedQty("");
    setPhasesRowData([]);
    setDisplayName("");
    setPhaseNameCode("");
    setOrderReference(defaultOrderReference);
    setEditPhases(false);
    setProjectProduct(product);
  };

  const handleChangeProject = (newValue, action) => {
    if (action.action === "clear") {
      phaseGridRef.current?.api?.deselectAll();
      setSelectedRows([]);
      setSelectedProName(null);
      setSelectedPhases("");
      setSelectedQty("");
      setPhasesRowData([]);
      setDisplayName("");
      setPhaseNameCode("");
      setOrderReference(defaultOrderReference);
      setEditPhases(false);
      setProjectProduct(product);
      return;
    }
    if (newValue?.__isNew__) {
      phaseGridRef.current?.api?.deselectAll();
      setSelectedRows([]);
      const normalizedValue = newValue.value.trim();
      const newOption = { value: normalizedValue, label: normalizedValue.toUpperCase(), __isNew__: true };
      setSelectedProName(newOption);
      setProjectOptions((prev) => [...prev, newOption]);
      setSelectedPhases("");
      setSelectedQty("");
      setPhasesRowData([]);
      setDisplayName("");
      setPhaseNameCode("");
      setOrderReference(defaultOrderReference);
      setEditPhases(false);
      setProjectProduct(product);
    } else {
      phaseGridRef.current?.api?.deselectAll();
      setSelectedRows([]);
      setPhaseNameCode("");
      setSelectedProName(newValue);
      const selectedVendor = productOptions.find(
        (option) => Number(option.id) === Number(newValue.vendorId)
      );

      setProjectProduct(selectedVendor || product);
      setSelectedPhases(newValue?.phases || "");
      setSelectedQty(newValue?.quantity || "");
      setPhasesRowData(newValue?.phasesData ? JSON.parse(JSON.stringify(newValue.phasesData)) : []);
      setPhasesTotalValue(newValue?.quantity || 0);
      setOrderReference(newValue?.orderReference || "");
      setDisplayName(newValue?.projectCode || "");
      setEditPhases(false);
    }
  };

  const handleChangeQty = (e) => {
    const value = Number(e.target.value);
    const isExisting = existingProjectOptions?.includes(selectedProName);

    if (hasSupperUser) {
      setSelectedQty(value);
      checkPhaseQtyMatch(value, phasesRowData);
      return;
    }

    if (isExisting && !hasSupperUser) {
      const maxAllowed = Math.floor(originalQty * 1.05);
      if (value < originalQty) {
        setSelectedQty(originalQty);
        showToastMsg(`You cannot reduce the overall quantity.`, "warning");
        return;
      }
      if (value > maxAllowed) {
        setSelectedQty(maxAllowed);
        showToastMsg(`Quantity cannot exceed +5%. Adjusted to ${maxAllowed}.`, "warning");
      } else {
        setSelectedQty(value);
      }
      checkPhaseQtyMatch(value > maxAllowed ? maxAllowed : value, phasesRowData);
      return;
    }
    setSelectedQty(value);
    checkPhaseQtyMatch(value, phasesRowData);
  };

  const handleBlur = () => {
    if (selectedQty < 10) {
      setSelectedQty(10);
      showToastMsg("Project order minimum quantity is 10", "warning");
    }
  };

  const checkPhaseQtyMatch = (totalQty, rows) => {
    if (!rows || rows.length === 0) {
      setShowPhasesAlert(false);
      return;
    }
    const totalPhaseQty = rows.reduce((sum, row) => sum + Number(row.phaseViseQty || 0), 0);
    if (totalQty !== totalPhaseQty) {
      setShowPhasesAlert(true);
      showToastMsg("Please adjust phase quantities to match the total quantity", "warning");
    } else {
      setShowPhasesAlert(false);
    }
  };

  const handleChangePhases = (e) => {
    const newPhaseCount = parseInt(e.target.value);
    if (!newPhaseCount || newPhaseCount <= 0) return;
    const isExistingProject = existingProjectOptions?.includes(selectedProName);
    const currentPhases = Array.isArray(phasesRowData) ? [...phasesRowData] : [];
    const currentCount = currentPhases.length;

    if (isExistingProject) {
      if (newPhaseCount <= currentCount) {
        showToastMsg("Phase count must be increased.", "warning");
        return;
      }
      const totalUsedQty = currentPhases.reduce((acc, row) => acc + Number(row.phaseViseQty || 0), 0);
      const remainingQty = selectedQty - totalUsedQty;
      const newRow = {
        phaseName: `Phase ${newPhaseCount}`,
        qty: 0,
        phaseViseQty: remainingQty > 0 ? remainingQty : 0,
        expectedProductionWeek: "",
        year: moment().add(7, "weeks").year(),
        orderPlaced: false,
      };
      const updatedRows = [...currentPhases, newRow];
      setPhasesRowData(updatedRows);
      setIsSectionVisible(false);
      setSelectedPhases(newPhaseCount);
      setPhasesTotalValue(updatedRows.reduce((sum, row) => sum + Number(row.phaseViseQty || 0), 0));
      checkPhaseQtyMatch(selectedQty, updatedRows);
    } else {
      const updatedRows = [];
      for (let i = 0; i < newPhaseCount; i++) {
        updatedRows.push({
          phaseName: `Phase ${i + 1}`,
          qty: 0,
          phaseViseQty: 0,
          expectedProductionWeek: "",
          year: moment().add(7, "weeks").year(),
          isCurrentPhase: true,
          orderPlaced: false,
        });
      }
      setPhasesRowData(updatedRows);
      setSelectedPhases(newPhaseCount);
      setPhasesTotalValue(0);
      setShowPhasesAlert(false);
    }
  };

  const handleUpdateProject = () => {
    if (selectedProName?.quantity) {
      setOriginalQty(selectedProName.quantity);
      setSelectedQty(selectedProName.quantity);
    }
    setEditPhases(true);
  };

  const handleRevertChanges = () => {
    if (!selectedProName) return;
    setEditPhases(false);
    setSelectedQty(selectedProName.quantity || "");
    setSelectedPhases(selectedProName.phases || "");
    setPhasesRowData(selectedProName.phasesData ? JSON.parse(JSON.stringify(selectedProName.phasesData)) : []);
    setPhasesTotalValue(selectedProName.quantity || 0);
    setOriginalQty(null);
    setPhaseNameCode("");
  };

  const hasChanges = useMemo(() => {
    return (
      selectedQty !== originalQty ||
      selectedPhases !== selectedProName?.phases ||
      JSON.stringify(phasesRowData) !== JSON.stringify(selectedProName?.phasesData)
    );
  }, [selectedQty, originalQty, selectedPhases, selectedProName?.phases, phasesRowData, selectedProName?.phasesData]);

  const handleChangeDisplayName = (event) => {
    let inputValue = event.target.value.toUpperCase();
    const maxLength = isTaiwan ? 2 : 4;

    if (inputValue.includes("/") || inputValue.includes("\\")) {
      showToastMsg("Project Short Name must not contain '/' or '\\'!");
      return;
    }
    const specialCharRegex = /^[a-zA-Z0-9()_\- ]*$/;
    if (!specialCharRegex.test(inputValue)) {
      showToastMsg("Special characters are not allowed!");
      return;
    }
    if (inputValue.length > TOTAL_MAX_LENGTH) {
      inputValue = inputValue.slice(0, TOTAL_MAX_LENGTH);
      showToastMsg(`Display Name cannot exceed ${TOTAL_MAX_LENGTH} characters!`, "warning");
    }
    setDisplayName(inputValue);
  };

  const showToastMsg = (msg, severity = "error") => {
    setToastMessage(msg);
    setToastSeverity(severity);
    setShowToast(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    const duration = Math.min(7000, Math.max(3000, msg.length * 60));
    toastTimerRef.current = setTimeout(() => setShowToast(false), duration);
  };

  const dismissToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setShowToast(false);
  };

  const showExcelErrorDialog = (title, rows) => {
    setExcelErrorDialog({ open: true, title, rows });
  };

  const closeExcelErrorDialog = () => {
    setExcelErrorDialog({ open: false, title: "", rows: [] });
  };

  const checkAllCellsFilled = () => {
    return phasesRowData.every((row) => row.phaseName && row.phaseName.toString().trim() !== "" && row.phaseViseQty && row.phaseViseQty > 0 && row.expectedProductionWeek);
  };

  const phasesColumnDefs = useMemo(() => [
    { field: "id", headerName: "Sno.", valueGetter: "node.rowIndex + 1", maxWidth: 70, hide: isMobile },
    { field: "phaseName", editable: (params) => !params.data.orderPlaced, valueFormatter: (params) => params.value ? params.value.toUpperCase() : "" },
    {
      field: "phaseViseQty", headerName: isMobile ? "Qty / Phase" : "Qty/Phase", maxWidth: 100, editable: (params) => !params.data.orderPlaced, cellEditor: NumericEditor,
      valueParser: (params) => { const v = parseFloat(params.newValue); return isNaN(v) || v < 0 ? null : v; }
    },
    {
      field: "expectedProductionWeek", headerName: "Expected Week", editable: (params) => !params.data.orderPlaced, cellEditorPopup: true, cellEditor: ExpectWeekEditor,
      suppressPaste: true
    },
    { field: "year", headerName: "Year", editable: false, hide: isMobile }
  ], [isMobile]);

  const defaultColDef = useMemo(() => ({ flex: 1, minWidth: isMobile ? 80 : 100, resizable: true, wrapHeaderText: isMobile, autoHeaderHeight: isMobile, checkboxSelection: (params) => params?.columnApi.getAllDisplayedColumns()[0] === params?.column }), [isMobile]);

  const autoGroupColumnDef = useMemo(() => ({ cellRendererParams: { checkbox: true } }), []);

  const onGridReadyPhaseGrid = useCallback((params) => {
    const { api } = params;
    if (phasesRowData.length > 0) {
      let firstSelectableIndex = phasesRowData.findIndex(r => !r.orderPlaced);
      if (firstSelectableIndex !== -1) {
        api.forEachNode((node) => {
          if (node.rowIndex === firstSelectableIndex) {
            node.setSelected(true);
            api.ensureIndexVisible(firstSelectableIndex);
            const phaseCode = `P${(firstSelectableIndex + 1).toString().padStart(2, "0")}`;
            setPhaseNameCode(phaseCode);
          }
        });
        setSelectedRows(api.getSelectedRows());
      }
    }
  }, [phasesRowData]);

  const onSelectionChanged = useCallback((params) => {
    const api = params.api;
    let selectedNode = api.getSelectedNodes()[0];
    setSelectedRows(api.getSelectedRows());
    if (selectedNode) setPhaseNameCode(`P${(selectedNode.rowIndex + 1).toString().padStart(2, "0")}`);
    else setPhaseNameCode("");
  }, []);

  const onCellValueChangedPhases = useCallback((event) => {
    if (event.colDef.field === "phaseName") {
      const trimmedValue = typeof event.newValue === "string" ? event.newValue.trim() : event.newValue;

      if (!trimmedValue) {
        event.node.setDataValue("phaseName", event.oldValue || `Phase ${event.node.rowIndex + 1}`);
        showToastMsg("Phase Name cannot be empty!");
        return;
      }

      if (trimmedValue !== event.newValue) {
        event.node.setDataValue("phaseName", trimmedValue);
        return;
      }
    }

    if (event.colDef.field === "expectedProductionWeek") {
      const newValue = event.newValue;
      if (!newValue) {
        const revertTo = event.oldValue || "";
        if (event.node.data.expectedProductionWeek !== revertTo) {
          showToastMsg("Invalid input! Please select a valid week from the list.");
          event.node.setDataValue("expectedProductionWeek", revertTo);
        }
        return;
      }
      const parsedYear = Number(newValue.split("/")[1]);
      if (event.node.data.year !== parsedYear) {
        event.node.setDataValue("year", parsedYear);
      }
    }

    if (event.colDef.field === "phaseViseQty") {
      const newValue = Number(event.newValue);
      if (isNaN(newValue) || newValue < 0) {
        showToastMsg("Invalid input! Only numeric values are allowed.");
        event.node.setDataValue("phaseViseQty", event.oldValue || 0);
        return;
      }

      const rowData = [];
      phaseGridRef.current.api.forEachNode((node) => rowData.push(node.data));
      const lastRowIndex = rowData.length - 1;

      let totalExcludingLast = 0;
      phaseGridRef.current.api.forEachNode((node, index) => {
        if (index !== lastRowIndex) totalExcludingLast += Number(node.data.phaseViseQty || 0);
      });
      const remainingQty = selectedQty - totalExcludingLast;
      if (remainingQty < 0) {
        event.node.setDataValue("phaseViseQty", event.oldValue);
        showToastMsg("Total quantity exceeds the overall quantity!");
      } else if (lastRowIndex >= 0) {
        phaseGridRef.current.api.getDisplayedRowAtIndex(lastRowIndex).setDataValue("phaseViseQty", remainingQty);
      }
    }

    const currentRows = [];
    phaseGridRef.current.api.forEachNode((node) => {
      currentRows.push(node.data);
    });
    const allFilled = currentRows.every(
      (row) =>
        row.phaseName &&
        row.phaseName.toString().trim() !== "" &&
        row.phaseViseQty > 0 &&
        row.expectedProductionWeek
    );

    setIsSectionVisible(allFilled);
    let newTotalValue = 0;
    phaseGridRef.current.api.forEachNode((node) => newTotalValue += Number(node.data.phaseViseQty || 0));
    setPhasesTotalValue(newTotalValue);
    setShowPhasesAlert(selectedQty !== newTotalValue);
  }, [selectedQty]);

  const normalizeExcelValue = (value) => (value === null || value === undefined ? "" : String(value).trim().toUpperCase());

  const normalizeExcelNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const num = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
    return isNaN(num) ? 0 : num;
  };

  const handleDownloadSample = () => {
    const b64 = "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";
    const byteChars = atob(b64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "master.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExcelUpload = async (e) => {
    const fileUploaded = e.target.files[0];
    if (!fileUploaded) return;

    if (!displayName || displayName.trim() === "") {
      showToastMsg("Project Short Name is mandatory.");
      hiddenExcelFile.current.value = null;
      return;
    }

    setLoadingExcel(true);
    const loadingStartedAt = Date.now();
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames.find(name => name.toLowerCase().includes("master")) || workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const normalizedData = jsonData.map(row => {
          const newRow = {};
          Object.keys(row).forEach(key => newRow[key.trim().toUpperCase()] = row[key]);
          return newRow;
        });
        const headers = Object.keys(normalizedData[0] || {});
        const requiredHeaders = ["TYPE", "UNIQUE ID", "LP WG1", "LP WG2", "VOLUME", "WEIGHT", "REASON", "POS NO", "ARTICLE CODE"];
        const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));
        if (missingHeaders.length > 0) {
          showToastMsg(`Missing required columns: ${missingHeaders.join(", ")}`);
          hiddenExcelFile.current.value = null;
          setLoadingExcel(false);
          return;
        }

        const allowedHeaders = [...requiredHeaders, "SUB CATEGORY", "REMARK"];
        const unknownHeaders = headers.filter((h) => !allowedHeaders.includes(h));
        if (unknownHeaders.length > 0) {
          showToastMsg(`Unexpected columns found: ${unknownHeaders.join(", ")}. Please use the Download Template for correct format.`);
          hiddenExcelFile.current.value = null;
          setLoadingExcel(false);
          return;
        }

        const mandatoryRowFields = ["TYPE", "UNIQUE ID", "REASON"];
        const invalidRows = [];

        normalizedData.forEach((row, index) => {
          const emptyFields = mandatoryRowFields.filter(
            (field) => !normalizeExcelValue(row[field])
          );
          if (emptyFields.length > 0) {
            invalidRows.push({ rowNum: index + 2, fields: emptyFields }); // +2: 1-indexed + header row
          }
        });

        if (invalidRows.length > 0) {
          showExcelErrorDialog(
            "Mandatory Fields Missing",
            invalidRows.map((r) => ({ rowNum: r.rowNum, issue: `Missing: ${r.fields.join(", ")}` }))
          );
          hiddenExcelFile.current.value = null;
          setLoadingExcel(false);
          return;
        }

        const numericRowFields = ["LP WG1", "LP WG2", "VOLUME", "WEIGHT"];
        const negativeRows = [];

        normalizedData.forEach((row, index) => {
          const negativeFields = numericRowFields.filter(
            (field) => normalizeExcelNumber(row[field]) < 0
          );
          if (negativeFields.length > 0) {
            negativeRows.push({ rowNum: index + 2, fields: negativeFields });
          }
        });

        if (negativeRows.length > 0) {
          showExcelErrorDialog(
            "Negative Values Not Allowed",
            negativeRows.map((r) => ({ rowNum: r.rowNum, issue: `Negative value in: ${r.fields.join(", ")}` }))
          );
          hiddenExcelFile.current.value = null;
          setLoadingExcel(false);
          return;
        }

        const maxCombinedLength = 20 - displayName.length;

        if (maxCombinedLength <= 0) {
          showToastMsg(`Project Short Name "${displayName}" is too long to leave room for TYPE + UNIQUE ID in the Commission Name. Please use a shorter Project Short Name.`);
          hiddenExcelFile.current.value = null;
          setLoadingExcel(false);
          return;
        }

        const oversizedRows = [];

        normalizedData.forEach((row, index) => {
          const type = normalizeExcelValue(row["TYPE"]);
          const uid = normalizeExcelValue(row["UNIQUE ID"]);
          if (type.length + uid.length > maxCombinedLength) {
            oversizedRows.push({ rowNum: index + 2 });
          }
        });

        if (oversizedRows.length > 0) {
          showExcelErrorDialog(
            "Commission Name Too Long",
            oversizedRows.map((r) => ({ rowNum: r.rowNum, issue: `TYPE + UNIQUE ID length must be ≤ ${maxCombinedLength}` }))
          );
          hiddenExcelFile.current.value = null;
          setLoadingExcel(false);
          return;
        }

        const reasonsRes = await OAuthService.getList(`${aoRoReasonsUrl}?size=1000`);
        const reasonsList = reasonsRes.data?._embedded?.aoRoReasons || [];
        const groupedData = {};

        normalizedData.forEach(row => {
          const uid = normalizeExcelValue(row["UNIQUE ID"]);
          if (!uid) return;

          if (!groupedData[uid]) {
            groupedData[uid] = {
              uniqueId: uid,
              typeNumber: normalizeExcelValue(row["TYPE"]),
              wg1: normalizeExcelNumber(row["LP WG1"]),
              wg2: normalizeExcelNumber(row["LP WG2"]),
              volume: normalizeExcelNumber(row["VOLUME"]),
              weight: normalizeExcelNumber(row["WEIGHT"]),
              range: normalizeExcelValue(row["RANGE"]),
              frontColor: normalizeExcelValue(row["FRONT COLOR"]),
              carcass: normalizeExcelValue(row["CARCASS"]),
              handle: normalizeExcelValue(row["HANDLE"]),
              partOrderDetails: []
            };
          }

          const reasonText = normalizeExcelValue(row["REASON"]);
          const matchedReason = reasonsList.find(r => r.reason.toUpperCase() === reasonText);
          const subCategoryText = String(row["SUB CATEGORY"] ?? "").trim();
          const reasonSubReasons = subCategoryText ? (matchedReason?.subReasons || []) : [];
          const exactSubCategory = reasonSubReasons.find(sub => String(sub).trim() === subCategoryText);
          const looseSubCategories = reasonSubReasons.filter(sub => String(sub).trim().toUpperCase() === subCategoryText.toUpperCase());
          const matchedSubCategory = exactSubCategory !== undefined
            ? exactSubCategory
            : (looseSubCategories.length === 1 ? looseSubCategories[0] : undefined);

          groupedData[uid].partOrderDetails.push({
            reason: matchedReason ? matchedReason.reason : "",
            reasonId: matchedReason ? matchedReason.reasonId : null,
            subReasons: matchedReason?.subReasons || [],
            subCategory: matchedSubCategory !== undefined ? String(matchedSubCategory) : "",
            articleCode: normalizeExcelValue(row["ARTICLE CODE"]),
            posNo: normalizeExcelValue(row["POS NO"]),
            remark: matchedReason
              ? normalizeExcelValue(row["REMARK"])
              : `[Excel Reason: ${row["REASON"]}] ${normalizeExcelValue(row["REMARK"])}`,
            imgUpldReqd: matchedReason?.imgUpldReqd ?? false,
            postnNumReqd: matchedReason?.postnNumReqd ?? false,
            picture: "",
            __rowId: Math.random().toString(36).substr(2, 9)
          });
        });

        const parsedOrders = Object.values(groupedData);

        if (parsedOrders.length === 0) {
          toast.error("No valid data found in the Excel sheet.");
          setLoadingExcel(false);
          hiddenExcelFile.current.value = null;
          return;
        }
        const expectedPhaseQty = selectedRows[0]?.phaseViseQty || 0;
        const isAoOnCompletedPhase = !!selectedRows[0]?.orderPlaced;

        if (!isAoOnCompletedPhase && parsedOrders.length !== expectedPhaseQty) {
          toast.error(`Phase quantity mismatch! Expected ${expectedPhaseQty} orders, but found ${parsedOrders.length} unique orders in the uploaded file.`);
          setLoadingExcel(false);
          hiddenExcelFile.current.value = null;
          return;
        }

        setOpen(false);
        navigate("/order/bulk-part-order-summary", {
          state: {
            globalData: {
              customerId: hasSupperUser ? selectedPartner?.value : customerId,
              showroomId: showroom.value,
              showroomName: showroom.label,
              product: projectProduct,
              orderCategoryValue,
              projectName: selectedProName.label,
              orderReference,
              containerNo,
              loadingWeek,
              freight,
              specialComments: comment,
              projectShortName: displayName,
              projectPhase: phaseNameCode,
              phasesData: phasesRowData,
              currentPhaseId: selectedRows[0]?.phaseName || null,
              existingTypeCount: selectedProName?.types?.length || 0,
            },
            parsedOrders: parsedOrders
          }
        });

      } catch (error) {
        console.error(error);
        toast.error("Error reading Excel file.");
      } finally {
        const elapsed = Date.now() - loadingStartedAt;
        const remaining = MIN_LOADING_VISIBLE_MS - elapsed;
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }
        setLoadingExcel(false);
        hiddenExcelFile.current.value = null;
      }
    };
    reader.readAsArrayBuffer(fileUploaded);
  };

  const handleOpenModal = () => {
    const missingGlobalFields = [];
    if (!showroom) missingGlobalFields.push("Showroom");
    if (!product) missingGlobalFields.push("Product");
    if (!orderCategoryValue) missingGlobalFields.push("Order Category");
    if (!freight) missingGlobalFields.push("Freight");
    if (missingGlobalFields.length > 0) {
      toast.warning(`Please fill out the following mandatory field(s): ${missingGlobalFields.join(", ")}.`);
      return;
    }
    if (!selectedProName || selectedProName.__isNew__) {
      setProjectProduct(product);
    } else {
      const selectedVendor = productOptions.find(
        (option) => Number(option.id) === Number(selectedProName.vendorId)
      );

      setProjectProduct(selectedVendor || product);
    }

    setOpen(true);
  };

  const projectAlreadyExists = existingProjectOptions.some(
    option =>
      option.label.trim().toUpperCase() ===
      currentQuery.trim().toUpperCase()
  );

  return (
    <>
      <FormPageHeader componentName="Bulk Part Order" pageName="Bulk Part Order" currentPage="Bulk Part Order" />
      <div className="px-12 sm:px-20">
        <Box className="mb-20 mt-20" sx={{ p: { xs: 1.5, sm: 2 }, border: `1px dashed ${theme.palette.divider}`, borderRadius: 4 }}>
          <Typography variant="h6" className="mb-10">Global Order Details</Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {hasSupperUser && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary" className="pb-8 block"><Box component="span" sx={{ color: "error.main" }}>*</Box>Partner</Typography>
                <Select value={selectedPartner} options={customerOptions} onChange={(e) => {
                  setSelectedPartner(e);
                  setShowroom(null);
                  setOrderCategoryValue(null);
                  setSelectedProName(null);
                  setProjectProduct(null);
                  setSelectedPhases("");
                  setSelectedQty("");
                  setPhasesRowData([]);
                  setDisplayName("");
                  setPhaseNameCode("");
                  setSelectedRows([]);
                  setEditPhases(false);
                  setProjectOptions([]);
                  setExistingProjectOptions([]);
                  setShowPhasesAlert(false);
                  setIsSectionVisible(false);
                  setPhasesTotalValue(0);
                  setOrderReference(defaultOrderReference);
                }}
                  styles={customStyles()} />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block"><Box component="span" sx={{ color: "error.main" }}>*</Box>Showroom</Typography>
              <Select value={showroom} options={showroomOptions} onChange={setShowroom} styles={customStyles()} isLoading={isFetchingConfig} isDisabled={isFetchingConfig || (hasSupperUser && !selectedPartner)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block"><Box component="span" sx={{ color: "error.main" }}>*</Box>Product</Typography>
              <Select value={product} options={productOptions} onChange={setProduct} styles={customStyles()} isLoading={isFetchingConfig} isDisabled={isFetchingConfig || (hasSupperUser && !selectedPartner)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block"><Box component="span" sx={{ color: "error.main" }}>*</Box>Order Category</Typography>
              <Select value={orderCategoryValue} options={orderCategoryOptions} onChange={handleChangeOrderCategory} styles={customStyles()} isLoading={isFetchingConfig} isDisabled={isFetchingConfig || (hasSupperUser && !selectedPartner)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block">Order Reference</Typography>
              <TextField fullWidth variant="outlined" value={orderReference} disabled />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block">Container No</Typography>
              <TextField fullWidth variant="outlined" value={containerNo} onChange={(e) => setContainerNo(e.target.value.toUpperCase())} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block">Loading Week</Typography>
              <Select value={loadingWeek} options={loadingWeekOptions} onChange={setLoadingWeek} styles={customStyles()} isLoading={isFetchingConfig} isDisabled={isFetchingConfig || (hasSupperUser && !selectedPartner)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block"><Box component="span" sx={{ color: "error.main" }}>*</Box>Freight</Typography>
              <Select value={freight} options={freightOptions} onChange={setFreight} styles={customStyles()} isDisabled={isFetchingConfig || (hasSupperUser && !selectedPartner)} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block">Special Comments</Typography>
              <TextField multiline fullWidth rows={2} value={comment} onChange={(e) => setComment(e.target.value.toUpperCase())} />
            </Grid>
            <Grid item xs={12} className="flex justify-end mt-10">
              <Button variant="contained" color="secondary" fullWidth={isMobile} onClick={handleOpenModal}>
                Proceed to Project Setup
              </Button>
            </Grid>
          </Grid>
        </Box>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg" fullScreen={isMobile}>
        <AppBar position="static">
          <Toolbar className="flex w-full justify-between">
            <Typography variant="subtitle1" color="inherit">Select or Create Project</Typography>
            <IconButton onClick={() => setOpen(false)} color="inherit" aria-label="Close dialog">
              <Icon>close</Icon>
            </IconButton>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ minHeight: { xs: 0, sm: 500 }, px: { xs: 1.5, sm: 3 }, color: theme.palette.text.primary }}>
          <ModalToast message={toastMessage} show={showToast} severity={toastSeverity} onClose={dismissToast} />
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block"><Box component="span" sx={{ color: "error.main" }}>*</Box>Project Name</Typography>
              <CreatableSelect
                value={selectedProName}
                onInputChange={handleProjectInputChange}
                onChange={handleChangeProject}
                isClearable
                placeholder="Type To Search Or Create..."
                isSearchable
                styles={customStyles()}
                maxMenuHeight={isMobile ? 200 : 300}
                className="capitalize"
                options={[
                  ...(
                    currentQuery && !projectAlreadyExists
                      ? [{
                        label: `Create "${currentQuery}"`,
                        value: currentQuery,
                        __isNew__: true
                      }]
                      : []
                  ),
                  ...existingProjectOptions,
                ]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block">*Product</Typography>
              <Select value={projectProduct} options={productOptions} styles={customStyles()} isDisabled />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block">PRJ Reference</Typography>
              <TextField value={orderReference} fullWidth variant="outlined" disabled />
            </Grid>
            <Grid item xs={6} md={4} lg={2}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block"><Box component="span" sx={{ color: "error.main" }}>*</Box>Overall Qty</Typography>
              <TextField
                value={selectedQty}
                onChange={handleChangeQty}
                onBlur={handleBlur}
                fullWidth
                variant="outlined"
                disabled={existingProjectOptions?.includes(selectedProName) && !editPhases}
                type="number"
                inputProps={{ min: originalQty || 10 }}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={2}>
              <Typography variant="subtitle2" color="text.secondary" className="pb-8 block"><Box component="span" sx={{ color: "error.main" }}>*</Box>No of Phases</Typography>
              <TextField
                value={selectedPhases}
                onChange={handleChangePhases}
                fullWidth
                variant="outlined"
                disabled={existingProjectOptions?.includes(selectedProName) && !editPhases}
                type="number"
              />
            </Grid>
            {showProjectEditControls && existingProjectOptions?.includes(selectedProName) && !editPhases && (
              <Grid item xs={12} md={4} lg={2}>
                <Button variant="contained" color="secondary" fullWidth={isMobile} onClick={handleUpdateProject} sx={{ ml: { xs: 0, lg: 1 } }}>Update</Button>
              </Grid>
            )}
            {showProjectEditControls && existingProjectOptions.includes(selectedProName) && editPhases && (
              <Grid item xs={12} md={4} lg={2}>
                <Button variant="outlined" color="secondary" fullWidth={isMobile} disabled={!hasChanges} onClick={handleRevertChanges} sx={{ ml: { xs: 0, lg: 1 } }}>Revert</Button>
              </Grid>
            )}
          </Grid>

          {selectedPhases && selectedProName && projectProduct && (
            <Box sx={{ my: 2 }}>
              {showPhasesAlert && (
                <Alert severity="error" sx={{ mb: 2 }} variant="outlined">
                  {phasesTotalValue > selectedQty
                    ? `Total phase quantity exceeds the overall quantity of ${selectedQty} by ${phasesTotalValue - selectedQty}.`
                    : `Total phase quantity is short of the overall quantity of ${selectedQty} by ${selectedQty - phasesTotalValue}.`}
                </Alert>
              )}
              <div className={theme.palette.mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"}>
                <AgGridReact
                  ref={phaseGridRef}
                  rowData={phasesRowData}
                  columnDefs={phasesColumnDefs}
                  defaultColDef={defaultColDef}
                  autoGroupColumnDef={autoGroupColumnDef}
                  rowSelection={"single"}
                  animateRows={true}
                  headerHeight={comfortableRows ? 44 : 32}
                  rowHeight={comfortableRows ? 40 : 28}
                  domLayout={"autoHeight"}
                  enableRangeSelection={true}
                  onCellValueChanged={onCellValueChangedPhases}
                  onSelectionChanged={onSelectionChanged}
                  getRowStyle={(params) => params.data.orderPlaced ? { background: alpha(theme.palette.success.main, 0.25) } : null}
                  isRowSelectable={(node) => {
                    if (!node.data) return false;
                    if (node.data.orderPlaced) return true;
                    const currentData = phasesDataRef.current || [];
                    const nodeIndex = currentData.findIndex(row => row.phaseName === node.data.phaseName);
                    if (nodeIndex <= 0) return true;
                    for (let i = 0; i < nodeIndex; i++) {
                      if (!currentData[i].orderPlaced) {
                        return false;
                      }
                    }
                    return true;
                  }}
                  onGridReady={onGridReadyPhaseGrid}
                />
              </div>
              <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "4px 12px", fontSize: 13, padding: "5px 10px", backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderTop: "none", color: theme.palette.text.primary }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "3px", backgroundColor: alpha(theme.palette.success.main, 0.25), border: `1px solid ${theme.palette.success.main}` }} />
                  <Typography variant="caption" color="text.secondary">Phase already ordered (locked)</Typography>
                </Box>
                <Box>Total Qty/Phases: <strong>{phasesTotalValue}</strong></Box>
              </Box>
            </Box>
          )}

          {selectedPhases && selectedRows.length > 0 && selectedProName && !isSectionVisible && phasesRowData.some((row) => !row.expectedProductionWeek) && (
            <Alert severity="warning" sx={{ my: 2 }} variant="outlined">
              Select an Expected Week for every phase to continue.
            </Alert>
          )}

          {selectedPhases && selectedRows.length > 0 && isSectionVisible && selectedProName && (
            <Box sx={{ my: 2 }}>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary" className="pb-8 block">Project Short Name</Typography>
                  <TextField name="displayName" fullWidth variant="outlined" type="text" value={displayName} onChange={handleChangeDisplayName} disabled={existingProjectOptions?.includes(selectedProName)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary" className="pb-8 block">Project Phase</Typography>
                  <TextField name="projectPhase" fullWidth variant="outlined" type="text" inputProps={{ maxLength: 3 }} value={phaseNameCode} disabled />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary" aria-hidden="true" sx={{ display: { xs: "none", sm: "block" }, pb: 1 }}>&nbsp;</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "start", gap: 0.5 }}>
                    <ColorButton variant="contained" component="label" className="w-full" sx={{ py: { xs: 1.25, sm: 0.75 } }} disabled={!displayName || loadingExcel}>
                      {loadingExcel ? (
                        <CircularProgress size={14} color="inherit" sx={{ display: "inline-block", mr: 1 }} />
                      ) : (
                        <FuseSvgIcon size={18} sx={{ display: "inline-block", mr: 1 }}>heroicons-outline:upload</FuseSvgIcon>
                      )}
                      {loadingExcel ? `Reading...` : "Upload"}
                      <input type="file" hidden onChange={handleExcelUpload} accept=".xlsx" ref={hiddenExcelFile} />
                    </ColorButton>
                    <Typography
                      variant="caption"
                      onClick={handleDownloadSample}
                      sx={{
                        cursor: "pointer",
                        py: { xs: 1, sm: 0 },
                        color: "primary.main",
                        textDecoration: "underline",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        "&:hover": { color: "primary.dark" }
                      }}
                    >
                      <FuseSvgIcon size={14}>heroicons-outline:download</FuseSvgIcon>
                      New to this? Download Template
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={excelErrorDialog.open} onClose={closeExcelErrorDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: "bold", color: theme.palette.error.main }}>
          {excelErrorDialog.title}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please fix the following row(s) in your Excel file and re-upload. Row numbers match the row in your
            spreadsheet (including the header row).
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: { xs: "none", sm: 360 } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: { xs: 64, sm: 100 } }}>Row</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Issue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {excelErrorDialog.rows.map((r, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{r.rowNum}</TableCell>
                    <TableCell sx={{ wordBreak: "break-word" }}>{r.issue}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closeExcelErrorDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkPartOrder;